import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../lib/api';

interface PullRequest {
  number: number;
  title: string;
  author: string;
  authorAvatar: string;
  branch: string;
  baseBranch: string;
  filesChanged: number;
  additions: number;
  deletions: number;
  createdAt: string;
  url: string;
  body: string | null;
}

interface PRPickerPanelProps {
  projectId: string;
  onSelect: (pr: PullRequest) => void;
}

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  padding: 16,
  marginBottom: 12,
  cursor: 'pointer',
  transition: 'border-color 150ms, box-shadow 150ms',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function PRPickerPanel({ projectId, onSelect }: PRPickerPanelProps) {
  const [prs, setPrs] = useState<PullRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; code?: string } | null>(null);
  const [selectedPR, setSelectedPR] = useState<PullRequest | null>(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const fetchPRs = useCallback(() => { setRefreshKey(k => k + 1); }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch<PullRequest[]>(`/projects/${projectId}/github/prs`);
        if (!cancelled) { setPrs(data); setError(null); setLoading(false); }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to fetch PRs';
        const code = msg.includes('not connected') ? 'GITHUB_NOT_CONNECTED' : undefined;
        setError({ message: msg, code });
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, refreshKey]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
          Loading pull requests...
        </p>
      </div>
    );
  }

  if (error?.code === 'GITHUB_NOT_CONNECTED') {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Connect GitHub in Settings → Organisation to enable PR-based code review.
        </p>
        <a
          href="/settings/organisation"
          style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--primary)', textDecoration: 'none' }}
        >
          Go to Settings →
        </a>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--error)', marginBottom: 12 }}>
          {error.message}
        </p>
        <button
          onClick={fetchPRs}
          style={{
            padding: '6px 16px', fontFamily: 'var(--font-body)', fontSize: 13,
            background: 'var(--primary)', color: 'white', border: 'none',
            borderRadius: 'var(--radius-sm)', cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Selected PR summary
  if (selectedPR) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{
          ...cardStyle, cursor: 'default',
          borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                #{selectedPR.number}
              </span>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: '4px 0' }}>
                {selectedPR.title}
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                {selectedPR.branch} → {selectedPR.baseBranch} · {selectedPR.filesChanged} files ·{' '}
                <span style={{ color: '#059669' }}>+{selectedPR.additions}</span>{' '}
                <span style={{ color: 'var(--error)' }}>-{selectedPR.deletions}</span>
              </p>
            </div>
            <button
              onClick={() => setSelectedPR(null)}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)',
                background: 'transparent', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', padding: '4px 10px', cursor: 'pointer',
              }}
            >
              Change
            </button>
          </div>
        </div>
        <button
          onClick={() => onSelect(selectedPR)}
          style={{
            width: '100%', padding: '10px 16px', fontFamily: 'var(--font-display)', fontSize: 14,
            fontWeight: 600, color: 'white', background: 'var(--primary)',
            border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
            marginTop: 8,
          }}
        >
          Start Code Review →
        </button>
      </div>
    );
  }

  // PR list
  if (prs.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>
          No open pull requests found in the connected repository.
        </p>
        <button
          onClick={fetchPRs}
          style={{
            padding: '6px 16px', fontFamily: 'var(--font-body)', fontSize: 13,
            background: 'var(--primary)', color: 'white', border: 'none',
            borderRadius: 'var(--radius-sm)', cursor: 'pointer',
          }}
        >
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
          Select a Pull Request to Review
        </h3>
        <button
          onClick={fetchPRs}
          style={{
            fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)',
            background: 'transparent', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', padding: '4px 10px', cursor: 'pointer',
          }}
        >
          Refresh
        </button>
      </div>

      {prs.map(pr => (
        <div
          key={pr.number}
          style={cardStyle}
          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--primary)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'; }}
          onClick={() => setSelectedPR(pr)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                  #{pr.number}
                </span>
                <span style={{
                  fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {pr.title.length > 60 ? pr.title.slice(0, 60) + '...' : pr.title}
                </span>
              </div>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
                {pr.author} · {pr.branch} → {pr.baseBranch} · {timeAgo(pr.createdAt)}
              </p>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>
                {pr.filesChanged} files ·{' '}
                <span style={{ color: '#059669' }}>+{pr.additions}</span>{' '}
                <span style={{ color: 'var(--error)' }}>-{pr.deletions}</span>
              </p>
            </div>
            <span style={{
              fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)',
              fontWeight: 500, whiteSpace: 'nowrap', marginLeft: 12,
            }}>
              Select →
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
